// Equipment Export/Import Utilities
// Handles .npb file format for equipment layouts

export interface EquipmentFile {
  version: string;
  format: 'npb-equipment';
  metadata: {
    name: string;
    description?: string;
    manufacturer?: string;
    model?: string;
    createdAt: string;
    exportedBy?: string;
  };
  layout: {
    simple?: any; // SimpleLayout
    multiFace?: any; // MultiFaceLayout
  };
  templates?: Record<string, any>; // Custom templates used
}

const CURRENT_VERSION = '1.0.0';

/**
 * Export equipment layout to .npb file
 */
export const exportEquipment = (
  layout: any,
  metadata: {
    name: string;
    description?: string;
    manufacturer?: string;
    model?: string;
  }
): EquipmentFile => {
  const equipmentFile: EquipmentFile = {
    version: CURRENT_VERSION,
    format: 'npb-equipment',
    metadata: {
      name: metadata.name,
      description: metadata.description,
      manufacturer: metadata.manufacturer,
      model: metadata.model,
      createdAt: new Date().toISOString(),
    },
    layout: {
      ...layout,
    },
    templates: {}, // Will be populated if custom templates are used
  };

  // Extract custom templates from layout if any
  if (layout.multiFace?.faces) {
    const customTemplates: Record<string, any> = {};
    
    Object.entries(layout.multiFace.faces).forEach(([face, faceLayout]: [string, any]) => {
      if (faceLayout?.templateSize && faceLayout.templateSize.startsWith('custom:')) {
        // Load custom template if not already included
        const templates = loadCustomTemplates();
        const templateId = faceLayout.templateSize;
        if (templates[templateId] && !customTemplates[templateId]) {
          customTemplates[templateId] = templates[templateId];
        }
      }
    });
    
    if (layout.simple?.templateSize && layout.simple.templateSize.startsWith('custom:')) {
      const templates = loadCustomTemplates();
      const templateId = layout.simple.templateSize;
      if (templates[templateId]) {
        customTemplates[templateId] = templates[templateId];
      }
    }
    
    if (Object.keys(customTemplates).length > 0) {
      equipmentFile.templates = customTemplates;
    }
  } else if (layout.simple?.templateSize && layout.simple.templateSize.startsWith('custom:')) {
    const templates = loadCustomTemplates();
    const templateId = layout.simple.templateSize;
    if (templates[templateId]) {
      equipmentFile.templates = {
        [templateId]: templates[templateId],
      };
    }
  }

  return equipmentFile;
};

/**
 * Download equipment file
 */
export const downloadEquipmentFile = (equipmentFile: EquipmentFile, filename: string) => {
  const json = JSON.stringify(equipmentFile, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.npb`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Import equipment from .npb file
 */
export const importEquipment = async (file: File): Promise<{ layout: any; templates: Record<string, any>; metadata: any }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const equipmentFile: EquipmentFile = JSON.parse(content);
        
        // Validate file format
        if (!equipmentFile.format || equipmentFile.format !== 'npb-equipment') {
          reject(new Error('Invalid file format. Expected .npb equipment file.'));
          return;
        }
        
        if (!equipmentFile.layout) {
          reject(new Error('File is missing layout data.'));
          return;
        }
        
        // Validate version compatibility (basic check)
        if (!equipmentFile.version) {
          reject(new Error('File is missing version information.'));
          return;
        }
        
        resolve({
          layout: equipmentFile.layout,
          templates: equipmentFile.templates || {},
          metadata: equipmentFile.metadata || {},
        });
      } catch (error) {
        reject(new Error(`Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file.'));
    };
    
    reader.readAsText(file);
  });
};

/**
 * Load custom templates from localStorage
 */
const loadCustomTemplates = (): Record<string, any> => {
  try {
    const stored = localStorage.getItem('netprobuilder-custom-templates');
    if (!stored) return {};
    return JSON.parse(stored);
  } catch {
    return {};
  }
};

/**
 * Save custom templates to localStorage
 */
export const saveCustomTemplates = (templates: Record<string, any>) => {
  try {
    localStorage.setItem('netprobuilder-custom-templates', JSON.stringify(templates));
  } catch (error) {
    console.error('Failed to save custom templates:', error);
    throw new Error('Failed to save custom templates');
  }
};





